import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import { emptyArray, useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import type {
  GetAgentMemoriesResponseBody,
  PatchAgentMemoryRequestBody,
} from "@app/types/api/assistant/configuration/memories";
import type { AgentConfigurationType } from "@app/types/assistant/agent";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Fetcher } from "swr";

export function useAgentMemoriesForUser({
  owner,
  agentConfiguration,
  disabled,
}: {
  owner: LightWorkspaceType;
  agentConfiguration: AgentConfigurationType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const memoriesFetcher: Fetcher<GetAgentMemoriesResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfiguration.sId}/memories`,
    memoriesFetcher,
    {
      disabled,
    }
  );

  return {
    memories: data?.memories ?? emptyArray(),
    isMemoriesLoading: !error && !data && !disabled,
    isMemoriesError: !!error,
    mutateMemories: mutate,
  };
}

export function useUpdateAgentMemory({
  owner,
  agentConfiguration,
}: {
  owner: LightWorkspaceType;
  agentConfiguration: AgentConfigurationType;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateMemories } = useAgentMemoriesForUser({
    owner,
    agentConfiguration,
    disabled: true,
  });

  const updateMemory = useCallback(
    async (memoryId: string, body: PatchAgentMemoryRequestBody) => {
      const res = await clientFetch(
        `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfiguration.sId}/memories/${memoryId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) {
        const json = await res.json();
        sendNotification({
          type: "error",
          title: t("toasts.agents.memoryUpdateFailedTitle"),
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          description:
            json.error?.message || t("toasts.agents.memoryUpdateFailedDesc"),
        });
        return null;
      }

      sendNotification({
        type: "success",
        title: t("toasts.agents.memoryUpdatedTitle"),
      });

      void mutateMemories();
      const json = await res.json();
      return json.memory;
    },
    [owner.sId, agentConfiguration, sendNotification, mutateMemories, t]
  );

  return { updateMemory };
}

export function useDeleteAgentMemory({
  owner,
  agentConfiguration,
}: {
  owner: LightWorkspaceType;
  agentConfiguration: AgentConfigurationType;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateMemories } = useAgentMemoriesForUser({
    owner,
    agentConfiguration,
    disabled: true,
  });

  const deleteMemory = useCallback(
    async (memoryId: string) => {
      const res = await clientFetch(
        `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfiguration.sId}/memories/${memoryId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const json = await res.json();
        sendNotification({
          type: "error",
          title: t("toasts.agents.memoryDeleteFailedTitle"),
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          description:
            json.error?.message || t("toasts.agents.memoryDeleteFailedDesc"),
        });
        return false;
      }

      sendNotification({
        type: "success",
        title: t("toasts.agents.memoryDeletedTitle"),
      });

      void mutateMemories();
      return true;
    },
    [owner.sId, agentConfiguration, sendNotification, mutateMemories, t]
  );

  return { deleteMemory };
}
